import { useEffect, useState } from 'react';
import { leadsFilter, LeadsProps } from '@/containers/Leads/types.ts';
import { useQuery } from '@tanstack/react-query';
import { useFetcher } from '@/api/useFetcher.ts';
import { Routes } from '@/api/types.ts';
import { generateApiPath } from '@/lib/utils.ts';
export const LeadActionEnum = {
  PRE_INTERVIEW: 'PRE_INTERVIEW',
  DECLINED: 'DECLINED',
  TEMP_TO_HIRE: 'TEMP_TO_HIRE',
} as const;
export type LeadActionType =
  (typeof LeadActionEnum)[keyof typeof LeadActionEnum];

export const EmployeeLeadStatus = {
  WAITING: 'WAITING', // from Employee

  SENT: 'SENT',
  RESPONDED: 'RESPONDED',
  OPENED: 'OPENED',
  AI_PROCESSED: 'AI_PROCESSED',
  DECLINED: 'DECLINED',
  RECEIVED: 'RECEIVED',
  CONFIRM_BY_EMPLOYEE: 'CONFIRM_BY_EMPLOYEE',
  VIEWED_BY_MANAGER: 'VIEWED_BY_MANAGER',
  DISTRIBUTED: 'DISTRIBUTED',
  STARTED: 'STARTED',
  PRE_INTERVIEW: 'PRE_INTERVIEW',
  PRE_INTERVIEW_RECEIVED: 'PRE_INTERVIEW_RECEIVED',
  PRE_INTERVIEW_CONFIRMED: 'PRE_INTERVIEW_CONFIRMED',
  TEMP_TO_HIRE: 'TEMP_TO_HIRE',
} as const;

export type EmployeeLeadStatus = typeof EmployeeLeadStatus;

const actionAllowedTransitionsFrom: Record<
  string,
  EmployeeLeadStatus[keyof EmployeeLeadStatus][]
> = {
  // # PRE APPROVED
  [EmployeeLeadStatus.PRE_INTERVIEW]: [
    // EmployeeLeadStatus.AI_PROCESSED,
    EmployeeLeadStatus.VIEWED_BY_MANAGER,
    EmployeeLeadStatus.DECLINED,
  ],
  [EmployeeLeadStatus.TEMP_TO_HIRE]: [
    // EmployeeLeadStatus.AI_PROCESSED,
    EmployeeLeadStatus.VIEWED_BY_MANAGER,
    EmployeeLeadStatus.DECLINED,
    EmployeeLeadStatus.PRE_INTERVIEW,
    EmployeeLeadStatus.PRE_INTERVIEW_RECEIVED,
    EmployeeLeadStatus.PRE_INTERVIEW_CONFIRMED,
  ],
  [EmployeeLeadStatus.DECLINED]: [
    // EmployeeLeadStatus.AI_PROCESSED,
    EmployeeLeadStatus.VIEWED_BY_MANAGER,
    EmployeeLeadStatus.PRE_INTERVIEW,
    EmployeeLeadStatus.PRE_INTERVIEW_RECEIVED,
    EmployeeLeadStatus.PRE_INTERVIEW_CONFIRMED,
  ],
};

export const isActionAvailable = (targetStatus: string, status: string) => {
  if (!(targetStatus in actionAllowedTransitionsFrom)) {
    return false;
  }
  return actionAllowedTransitionsFrom[targetStatus].includes(
    status as EmployeeLeadStatus[keyof EmployeeLeadStatus]
  );
};

export const statusColorMapping: Record<
  EmployeeLeadStatus[keyof EmployeeLeadStatus],
  string
> = {
  [EmployeeLeadStatus.SENT]: '#467ADF',
  [EmployeeLeadStatus.OPENED]: '#467ADF',
  [EmployeeLeadStatus.STARTED]: '#467ADF',
  [EmployeeLeadStatus.RESPONDED]: '#467ADF',
  [EmployeeLeadStatus.AI_PROCESSED]: '#EB5757',
  [EmployeeLeadStatus.WAITING]: '#EB5757',
  [EmployeeLeadStatus.VIEWED_BY_MANAGER]: '#EB5757',
  [EmployeeLeadStatus.DECLINED]: '#EB5757',
  [EmployeeLeadStatus.PRE_INTERVIEW]: '#467ADF',
  [EmployeeLeadStatus.PRE_INTERVIEW_RECEIVED]: '#467ADF',
  [EmployeeLeadStatus.RECEIVED]: '#FFAE00',
  [EmployeeLeadStatus.CONFIRM_BY_EMPLOYEE]: '#219653',
  [EmployeeLeadStatus.PRE_INTERVIEW_CONFIRMED]: '#219653',
  [EmployeeLeadStatus.TEMP_TO_HIRE]: '#219653',
};

type EmployeeLeadsResponse = {
  data: {
    count: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    results: any[];
  };
};

const useEmployeeLeads = (filter: LeadsProps['filter'], status: string[]) => {
  const [page, setPage] = useState<number>(1);

  const statusString = status.join(',');
  const fetchData = useFetcher();

  useEffect(() => {
    setPage(1);
  }, [statusString, filter]);

  const { data, refetch } = useQuery({
    queryKey: ['/manager/leads', filter, statusString, page],
    queryFn: () =>
      fetchData<EmployeeLeadsResponse>(
        generateApiPath(Routes.LeadsList, {
          type: filter === leadsFilter.inhouse ? 'inhouse' : 'by-agency',
        }) + `?status=${statusString}&page=${page}`
      ),
  });

  return {
    leads: data?.data ?? {
      count: 0,
      results: [],
    },
    invalidate: refetch,
    page,
    onPageChange: setPage,
  } as const;
};

export default useEmployeeLeads;
